import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { Download, FileText, Settings as Cog, IndianRupee, CheckSquare, Loader2 } from 'lucide-react';
import { inrCompact } from '../../lib/format';
import { toast } from 'sonner';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const TABS = [
  { key: 'pf', label: 'PF Register', endpoint: '/hr/statutory/pf-register', period: 'month' },
  { key: 'esi', label: 'ESI Register', endpoint: '/hr/statutory/esi-register', period: 'month' },
  { key: 'pt', label: 'PT Register', endpoint: '/hr/statutory/pt-register', period: 'month' },
  { key: 'tds', label: 'TDS Register', endpoint: '/hr/statutory/tds-register', period: 'fy' },
];

export default function StatutoryRegisters() {
  const [tab, setTab] = useUrlTab('tab', 'pf');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [fy, setFy] = useState('2025-26');
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);

  const active = TABS.find(t => t.key === tab);

  const load = async () => {
    try {
      const period = active.period === 'month' ? `month=${month}` : `fy=${fy}`;
      const res = await apiGet(`${active.endpoint}?${period}`);
      setData(res);
    } catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, [tab, month, fy]);
  useEffect(() => { apiGet('/hr/statutory/settings').then(setSettings); }, []);

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const headers = Object.keys(data.rows[0]);
    const csv = [headers.join(','), ...data.rows.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tab}_register_${active.period === 'month' ? month : fy}.csv`;
    a.click();
  };

  // Prompt-1 — finalise the payroll month (idempotent; fires GL event).
  const [finalising, setFinalising] = useState(false);
  const finaliseMonth = async () => {
    if (!window.confirm(`Finalise payroll for ${month}?\n\nThis locks the month and posts the payroll event to the Shadow GL. The action is idempotent — re-running it will not create a second journal.`)) return;
    setFinalising(true);
    try {
      const r = await apiPost('/hr/payroll/finalise-month', { month });
      if (r.already_finalised) toast.info(`${month} was already finalised by ${r.finalisation?.finalised_by || '—'}`);
      else toast.success(`Payroll for ${month} finalised — GL ref ${r.finalisation?.gl_jv_no || '—'}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Finalise failed'); }
    finally { setFinalising(false); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Payroll · Compliance</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Statutory Registers</h1>
          <p className="text-sm text-slate-600 mt-1">PF · ESI · Professional Tax · TDS — auto-generated from monthly payroll.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={finaliseMonth} disabled={finalising} className="btn-secondary flex items-center gap-2 disabled:opacity-50" data-testid="finalise-month-btn">
            {finalising ? <Loader2 size={14} className="animate-spin" /> : <CheckSquare size={14} />} Finalise Month
          </button>
          <button onClick={exportCsv} className="btn-secondary flex items-center gap-2" disabled={!data?.rows?.length} data-testid="export-csv-btn">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="border-b border-slate-200 flex gap-1">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2 text-sm font-display font-semibold transition-colors border-b-2 ${tab === t.key ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-600 hover:text-[#0F172A]'}`} data-testid={`tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>
        {active?.period === 'month' ? (
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="input text-sm w-44" data-testid="register-month-input" />
        ) : (
          <select value={fy} onChange={(e) => setFy(e.target.value)} className="input text-sm w-32">
            <option>2024-25</option><option>2025-26</option><option>2026-27</option>
          </select>
        )}
      </div>

      {/* Settings summary */}
      {settings && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap items-center gap-6 text-xs text-slate-600">
          <div className="flex items-center gap-1 font-display font-semibold text-slate-700"><Cog size={12} /> Active Rules:</div>
          <span>PF Emp {settings.pf_employee_rate}% / Cap ₹{settings.pf_basic_cap.toLocaleString('en-IN')}</span>
          <span>ESI Emp {settings.esi_employee_rate}% / Above ₹{settings.esi_gross_limit.toLocaleString('en-IN')} excluded</span>
          <span>PT {settings.pt_state}</span>
          <span>Std Deduction Old ₹{settings.standard_deduction_old.toLocaleString('en-IN')} · New ₹{settings.standard_deduction_new.toLocaleString('en-IN')}</span>
        </div>
      )}

      {/* Totals */}
      {data?.totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {Object.entries(data.totals).map(([k, v]) => (
            <div key={k} className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">{k.replace(/_/g, ' ')}</div>
              <div className="font-display text-2xl font-extrabold text-[#3b4872] mt-1">₹{(v || 0).toLocaleString('en-IN')}</div>
            </div>
          ))}
        </div>
      )}

      {/* Rows */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {data?.rows?.length ? (
          <table className="w-full dense-table">
            <thead><tr>{Object.keys(data.rows[0]).map(h => <th key={h} className="capitalize">{h.replace(/_/g, ' ')}</th>)}</tr></thead>
            <tbody>
              {data.rows.map((row, i) => (
                <tr key={i} data-testid={`register-row-${i}`}>
                  {Object.entries(row).map(([k, v]) => (
                    <td key={k} className={typeof v === 'number' ? 'text-right font-display font-bold' : 'text-xs'}>
                      {typeof v === 'number' ? (v >= 1000 ? '₹' + v.toLocaleString('en-IN') : v) : (v || '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-10 text-center text-sm text-slate-500">
            <FileText size={28} className="mx-auto mb-2 text-slate-400" />
            No payroll data for this period. Run payroll for {active?.period === 'month' ? month : fy} first.
          </div>
        )}
      </div>
    </div>
  );
}
